import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
// Admin: list all tournaments
export function useListTournaments() {
    return useQuery({
        queryKey: ['tournaments'],
        queryFn: () => api.request('/api/tournaments'),
    });
}
// Admin: get single tournament with entries
export function useGetTournament(tournamentId) {
    return useQuery({
        queryKey: ['tournaments', tournamentId],
        queryFn: () => api.request(`/api/tournaments/${tournamentId}`),
        enabled: tournamentId > 0,
    });
}
// Admin: create tournament
export function useCreateTournament() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => api.request('/api/tournaments', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['tournaments'] }),
    });
}
// Admin: patch tournament metadata
export function useUpdateTournament() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ tournamentId, body, }) => api.request(`/api/tournaments/${tournamentId}`, {
            method: 'PATCH',
            body: JSON.stringify(body),
        }),
        onSuccess: (_data, { tournamentId }) => {
            void qc.invalidateQueries({ queryKey: ['tournaments'] });
            void qc.invalidateQueries({ queryKey: ['tournaments', tournamentId] });
        },
    });
}
// Admin: transition tournament status
export function useTransitionTournament() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ tournamentId, body, }) => api.request(`/api/tournaments/${tournamentId}/transition`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        onSuccess: (_data, { tournamentId }) => {
            void qc.invalidateQueries({ queryKey: ['tournaments'] });
            void qc.invalidateQueries({ queryKey: ['tournaments', tournamentId] });
        },
    });
}
// Admin: finalize tournament (compute final standings + ELO)
export function useFinalizeTournament() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (tournamentId) => api.request(`/api/tournaments/${tournamentId}/finalize`, {
            method: 'POST',
        }),
        onSuccess: (_data, tournamentId) => {
            void qc.invalidateQueries({ queryKey: ['tournaments'] });
            void qc.invalidateQueries({ queryKey: ['tournaments', tournamentId] });
        },
    });
}
// Admin: upsert entries (add players to tournament)
export function useUpsertEntries() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ tournamentId, body, }) => api.request(`/api/tournaments/${tournamentId}/entries`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        onSuccess: (_data, { tournamentId }) => qc.invalidateQueries({ queryKey: ['tournaments', tournamentId] }),
    });
}
// Admin: update seeding
export function useUpdateSeeding() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ tournamentId, body, }) => api.request(`/api/tournaments/${tournamentId}/seeding`, {
            method: 'PATCH',
            body: JSON.stringify(body),
        }),
        onSuccess: (_data, { tournamentId }) => qc.invalidateQueries({ queryKey: ['tournaments', tournamentId] }),
    });
}
// Admin: toggle anonymous mode
export function useToggleAnonymize(tournamentId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => api.request(`/api/tournaments/${tournamentId}/anonymize`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ['tournaments', tournamentId] });
            void qc.invalidateQueries({ queryKey: ['public-tournaments'] });
        },
    });
}
// Public: list non-draft, non-archived tournaments
export function usePublicTournaments() {
    return useQuery({
        queryKey: ['public-tournaments'],
        queryFn: () => api.request('/api/public/tournaments'),
    });
}
// Public: get tournament by slug
export function usePublicTournament(slug) {
    return useQuery({
        queryKey: ['public-tournaments', slug],
        queryFn: () => api.request(`/api/public/tournaments/${slug}`),
        enabled: slug.length > 0,
    });
}
